// The on-map action bar: a compact DOM toolbar anchored next to whatever is
// selected on the scope -- Delay/Reroute for a filed plan, Hold/Clear for an
// airborne drone -- with the keyboard shortcut shown on each button. For a
// plan, each option says up front what it would do (a verdict from
// previewAdjustment), and hovering or focusing Reroute previews the new route
// on the scope.
//
// Built once and updated in place (like the side lists), so buttons never
// get swapped out from under the pointer. While the pointer is over the bar
// it stops following its target, so a moving drone can't pull a button away
// mid-click.

import {
  DELAY_STEP, PREDICT_URGENT, FIELDS, HUBS, SPRAY_WIND_MIN, SPRAY_WIND_MAX, POWER,
} from '../config/constants.js';
import { opsType } from '../core/ops.js';
import { $, setText, setClass, setHidden } from './dom.js';

const GAP = 14;    // px between the anchor point and the bar
const MARGIN = 6;  // px kept clear of the scope's edges
// corners the bar can sit in, relative to its anchor; below-right first
const CORNERS = [{ x: 1, y: 1 }, { x: -1, y: 1 }, { x: 1, y: -1 }, { x: -1, y: -1 }];

export const SHORTCUTS = { d: 'delay', r: 'reroute', h: 'hold', c: 'clear', b: 'return', e: 'emergency' };

export const DRAG_HINT = 'Drag the route to another hub to reroute';

const calls = (list) => list.map((c) => c.call).join(', ');

/**
 * One-line verdict for a Delay or Reroute preview (see previewAdjustment):
 * { tone: 'good' | 'bad' | 'wind' | 'neutral', text }.
 */
export function adjustmentVerdict(plan, pv) {
  if (!pv) return { tone: 'neutral', text: '' };
  const v = baseVerdict(plan, pv);
  // a reroute to a hub without its supply works, but costs a long turnaround (#32)
  if (pv.noSupply && v.tone !== 'wind') {
    const supply = POWER[plan.power]?.name ?? plan.power;
    return { tone: v.tone === 'bad' ? 'bad' : 'wind', text: `${v.text} · ${pv.hub} has no ${supply}: long turnaround` };
  }
  return v;
}

function baseVerdict(plan, pv) {
  if (pv.grounded) {
    return {
      tone: 'wind',
      text: pv.kind === 'delay'
        ? `Moves it into a ${pv.windAtDepart}mph slot: grounded`
        : `Grounded: ${pv.windAtDepart}mph is over the ${plan.type} limit`,
    };
  }
  if (pv.remaining.length && pv.created.length) {
    return { tone: 'bad', text: `Still conflicts with ${calls(pv.remaining)}; creates one with ${calls(pv.created)}` };
  }
  if (pv.created.length && pv.cleared.length) {
    return { tone: 'bad', text: `Clears ${calls(pv.cleared)} but creates a conflict with ${calls(pv.created)}` };
  }
  if (pv.created.length) return { tone: 'bad', text: `Creates a conflict with ${calls(pv.created)}` };
  if (pv.remaining.length) return { tone: 'bad', text: `Still conflicts with ${calls(pv.remaining)}` };
  if (pv.cleared.length) return { tone: 'good', text: `Clears the conflict with ${calls(pv.cleared)}` };
  if (plan.grounded) return { tone: 'good', text: 'Lifts the wind hold' };
  return { tone: 'neutral', text: 'No conflict either way' };
}

export function createActionBar(el, { onAction, onClose }) {
  el.innerHTML = `
    <div class="ab-head">
      <span class="ab-call"></span>
      <span class="ab-status"></span>
      <button type="button" class="ab-close" data-action="close" aria-label="Deselect" title="Deselect (Esc)">&times;</button>
    </div>
    <div class="ab-route"><b class="from"></b> &rarr; <b class="to"></b></div>
    <div class="ab-note"></div>
    <div class="ab-actions ab-plan" data-for="plan">
      <div class="ab-opt">
        <button type="button" class="delay" data-action="delay">Delay +${DELAY_STEP}s <kbd>D</kbd></button>
        <span class="ab-verdict" data-verdict="delay"></span>
      </div>
      <div class="ab-opt">
        <button type="button" class="reroute" data-action="reroute"><span class="lbl">Reroute</span> <kbd>R</kbd></button>
        <span class="ab-verdict" data-verdict="reroute"></span>
      </div>
      <div class="ab-hint">${DRAG_HINT}</div>
    </div>
    <div class="ab-actions" data-for="drone">
      <button type="button" class="hold" data-action="hold">Hold <kbd>H</kbd></button>
      <button type="button" class="clear" data-action="clear">Clear <kbd>C</kbd></button>
    </div>
    <div class="ab-actions ab-emergency" data-for="emergency" hidden>
      <button type="button" class="emergency" data-action="emergency">Land at nearest free pad <kbd>E</kbd></button>
    </div>
    <div class="ab-actions ab-ops" data-for="ops">
      <div class="ab-hint ab-ops-hint"></div>
      <div class="ab-row">
        <button type="button" class="hold" data-action="hold">Hold <kbd>H</kbd></button>
        <button type="button" class="clear" data-action="clear">Clear <kbd>C</kbd></button>
        <button type="button" class="return" data-action="return">Return to pad <kbd>B</kbd></button>
      </div>
    </div>`;

  const parts = {
    call: $('.ab-call', el),
    status: $('.ab-status', el),
    from: $('.from', el),
    to: $('.to', el),
    note: $('.ab-note', el),
    planActions: $('[data-for="plan"]', el),
    droneActions: $('[data-for="drone"]', el),
    emergencyActions: $('[data-for="emergency"]', el),
    hold: $('[data-for="drone"] button.hold', el),
    clear: $('[data-for="drone"] button.clear', el),
    opsActions: $('[data-for="ops"]', el),
    opsHint: $('.ab-ops-hint', el),
    opsHold: $('[data-for="ops"] button.hold', el),
    opsClear: $('[data-for="ops"] button.clear', el),
    opsReturn: $('[data-for="ops"] button.return', el),
    rerouteLabel: $('button.reroute .lbl', el),
    verdicts: { delay: $('[data-verdict="delay"]', el), reroute: $('[data-verdict="reroute"]', el) },
  };

  // Which option the pointer or keyboard focus is on, so the scope can
  // preview it.
  let pointed = null;
  let focused = null;
  for (const action of ['delay', 'reroute']) {
    const btn = $(`button.${action}`, el);
    btn.addEventListener('pointerenter', () => { pointed = action; });
    btn.addEventListener('pointerleave', () => { if (pointed === action) pointed = null; });
    btn.addEventListener('focus', () => { focused = action; });
    btn.addEventListener('blur', () => { if (focused === action) focused = null; });
  }

  let hovered = false;
  let placed = { x: NaN, y: NaN };
  let lastKey = '';
  el.addEventListener('pointerenter', () => { hovered = true; });
  el.addEventListener('pointerleave', () => { hovered = false; });
  el.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-action]');
    if (!btn || btn.disabled) return;
    if (btn.dataset.action === 'close') onClose();
    else onAction(btn.dataset.action);
  });

  function describePlan(p, elapsed) {
    const secs = Math.max(0, Math.ceil(p.departAt - elapsed));
    let kind = 'clear';
    let status = `T-${secs}s`;
    let note = 'No conflict predicted';
    if (p.grounded) {
      kind = 'wind';
      status = p.windHeld ? 'Wind hold' : `T-${secs}s`;
      note = `Grounded: ${p.windAtDepart}mph forecast is over the ${p.type} limit (${p.maxWind})`;
    } else if (p.predicted) {
      kind = p.departAt - elapsed < PREDICT_URGENT ? 'urgent' : 'predicted';
      note = `⚠ Launches into a conflict with ${p.conflictWith.join(', ')}`;
    }
    return { kind, status, note };
  }

  function describeDrone(d) {
    let status = { holding: 'Holding', conflict: 'Conflict', enroute: 'En route', landed: 'On pad' }[d.state];
    let note = {
      holding: 'Holding pattern: safe from collisions, not landing',
      conflict: '⚠ Inside the separation ring',
      enroute: d.divertZone ? `Diverting to ${d.destLabel}` : 'Flying its filed route',
      landed: d.noSupply
        ? `On a pad with no ${POWER[d.power]?.name ?? 'supply'}: waiting ${Math.max(0, Math.ceil(d.landLeft ?? 0))}s for a swap`
        : `Landed: on the pad for ${Math.max(0, Math.ceil(d.landLeft ?? 0))}s more`,
    }[d.state];
    if (d.padWait && d.state === 'holding') {
      status = 'Pattern';
      note = 'Circling: its hub is full. Click a field pad (P) to land it there';
    }
    if (d.emergency) {
      return { kind: 'conflict', status: 'Emergency', note: `⚠ Must land within ${Math.ceil(d.emergency.left)}s` };
    }
    return { kind: d.state === 'landed' ? 'enroute' : d.state, status, note };
  }

  // The farmer's own aircraft (#13): what it's doing, and what clicking a
  // field would send it to do.
  function describeOps(a) {
    const t = opsType(a.type);
    const field = a.task ? FIELDS[a.task.fieldIdx].name : null;
    const job = a.task?.kind === 'scout' ? 'Scouting' : 'Spraying';
    const status = {
      parked: 'Parked', outbound: 'Outbound', working: job, sprayHold: 'Spray hold', returning: 'Returning', holding: 'Holding',
      peopleHold: 'Person in field', padHold: 'Pad blocked', recharging: a.power === 'ev' ? 'Charging' : 'Refueling',
    }[a.state];
    const note = {
      parked: `Ready at ${a.padName}`,
      outbound: `Flying to ${field}`,
      working: `${job} ${field}`,
      sprayHold: `Waiting at ${field} for ${SPRAY_WIND_MIN}–${SPRAY_WIND_MAX} mph wind`,
      returning: `Heading back to ${a.padName}`,
      holding: 'Holding pattern: safe from collisions and birds, not working',
      peopleHold: `Spraying paused: someone is in ${field}`,
      padHold: `Hovering: the pad at ${a.padName} is blocked. Send it to another field, or the tow crew`,
      recharging: `${a.power === 'ev' ? 'Charging' : 'Refueling'} at ${a.padName}`,
    }[a.state];
    // battery or fuel left, and whether its base can refill it (#32)
    const p = POWER[a.power];
    const base = a.hubIdx !== null && HUBS[a.hubIdx].supplies.includes(a.power) ? '' : ` · no ${p.name} at ${a.padName}`;
    const energy = `${p.icon} ${p.label} ${Math.round(a.energy * 100)}%${base}`;
    const hint = `${t.task === 'scout' ? 'Click a field to scout it clear' : 'Click a scouted (clear) field to spray it'}, or a pad (P) to base it there`;
    let warn = null;
    if (a.emergency) warn = `⚠ Emergency: must land within ${Math.ceil(a.emergency.left)}s`;
    else if (a.alert) warn = '⚠ Inside the separation ring';
    else if (a.predict) warn = `⚠ Meets ${a.predict.with} in ${Math.ceil(a.predict.in)}s: Hold one until the other passes`;
    return {
      kind: a.alert ? 'conflict' : (a.predict ? 'predicted' : a.state), status, note: warn ?? `${note} · ${energy}`, hint,
      from: a.padName, to: field ?? (a.state === 'parked' ? '—' : a.padName),
    };
  }

  // Place the bar in the corner beside the anchor that best keeps it off the
  // routes it's about (`avoid`: points in px the flight is heading for or a
  // reroute would use), and fully on the scope. Sticks with its current corner
  // unless another is clearly better, so it doesn't flicker.
  let corner = null;
  function place(anchor, size, avoid) {
    const bw = el.offsetWidth;
    const bh = el.offsetHeight;
    const dirs = avoid
      .map((pt) => ({ x: pt.x - anchor.x, y: pt.y - anchor.y }))
      .filter((v) => Math.hypot(v.x, v.y) > 1)
      .map((v) => { const d = Math.hypot(v.x, v.y); return { x: v.x / d, y: v.y / d }; });
    const options = CORNERS.map((c) => {
      const x = c.x > 0 ? anchor.x + GAP : anchor.x - GAP - bw;
      const y = c.y > 0 ? anchor.y + GAP : anchor.y - GAP - bh;
      const fits = x >= MARGIN && y >= MARGIN && x + bw <= size.w - MARGIN && y + bh <= size.h - MARGIN;
      const overlap = dirs.reduce((sum, d) => sum + Math.max(0, d.x * c.x + d.y * c.y), 0);
      return { c, x, y, score: overlap + (fits ? 0 : 10) };
    });
    let pick = options.reduce((a, b) => (b.score < a.score ? b : a));
    const current = options.find((o) => o.c === corner);
    if (current && current.score <= pick.score + 0.3) pick = current;
    corner = pick.c;
    const x = Math.round(Math.max(MARGIN, Math.min(size.w - bw - MARGIN, pick.x)));
    const y = Math.round(Math.max(MARGIN, Math.min(size.h - bh - MARGIN, pick.y)));
    if (x !== placed.x || y !== placed.y) {
      el.style.transform = `translate(${x}px, ${y}px)`;
      placed = { x, y };
    }
  }

  function showVerdict(action, plan, pv) {
    const v = adjustmentVerdict(plan, pv);
    const node = parts.verdicts[action];
    setClass(node, `ab-verdict ${v.tone}`);
    setText(node, v.text);
  }

  return {
    /** 'delay' or 'reroute' while that button is hovered or focused, else null. */
    previewing: () => (el.hidden ? null : pointed || focused),

    /**
     * `target` is the selected plan or drone (or null), `kind` 'plan' | 'drone',
     * `anchor` its position on the scope in CSS px, `size` the scope's size,
     * `previews` the plan's { delay, reroute } previews, `avoid` points (px)
     * the bar should keep clear of.
     */
    update(target, kind, anchor, elapsed, size, previews = {}, avoid = []) {
      setHidden(el, !target);
      if (!target) { hovered = false; lastKey = ''; return; }
      const info = kind === 'plan' ? describePlan(target, elapsed) : (kind === 'ops' ? describeOps(target) : describeDrone(target));
      setClass(el, `action-bar ${kind} ${info.kind}`);
      el.style.setProperty('--type-color', target.color);
      setText(parts.call, target.call);
      setText(parts.status, info.status);
      setText(parts.from, kind === 'ops' ? info.from : target.originLabel);
      setText(parts.to, kind === 'ops' ? info.to : target.destLabel);
      setText(parts.note, info.note);
      setHidden(parts.planActions, kind !== 'plan');
      setHidden(parts.droneActions, kind !== 'drone');
      setHidden(parts.opsActions, kind !== 'ops');
      if (kind === 'ops') {
        const holding = target.state === 'holding';
        const parked = target.state === 'parked' || target.state === 'recharging'; // on the ground
        setText(parts.opsHint, info.hint);
        if (parts.opsHold.disabled !== (holding || parked)) parts.opsHold.disabled = holding || parked;
        if (parts.opsClear.disabled !== !holding) parts.opsClear.disabled = !holding;
        if (parts.opsReturn.disabled !== parked) parts.opsReturn.disabled = parked;
      }
      if (kind === 'plan') {
        setText(parts.rerouteLabel, previews.reroute ? `Reroute via ${previews.reroute.hub}` : 'Reroute');
        showVerdict('delay', target, previews.delay);
        showVerdict('reroute', target, previews.reroute);
      }
      if (kind === 'drone') {
        const holding = target.state === 'holding';
        const landed = target.state === 'landed';
        if (parts.hold.disabled !== (holding || landed)) parts.hold.disabled = holding || landed;
        if (parts.clear.disabled !== !holding) parts.clear.disabled = !holding;
      }
      setHidden(parts.emergencyActions, !target.emergency);
      // a new selection always moves the bar; the same one stays put under the pointer
      const key = `${kind}:${target.id}`;
      if (key !== lastKey) corner = null;
      if (!hovered || key !== lastKey) place(anchor, size, avoid);
      lastKey = key;
    },
  };
}
